import { Icon } from '@synergy/icons';
import { Col, Flex } from "antd";
import { useEffect, useState } from 'react';
import { useTranslation } from "react-i18next";
import styled from 'styled-components';
import { useGlobalData } from '../../../../../contexts/GlobalDataProvider';
import query from '../../../../../server/constants.api';
import useFetch from '../../../../../server/useFetch';
import { handleNotificationWithAction } from '../../../../../utils/config/notificationHandler';
import { Text } from '../../../Atomics/Text';
import { EditPopupConfigurations } from "../../Configurations/EditPopupConfigurations";
import useCreateIncidentRecord from './useCreateIncidentRecord/useCreateIncidentRecord';

const HeaderContainer = styled.div`
    margin-bottom: 16px;
`

const ConfidentialWrapper = styled.div`
    display: flex;
    flex-direction: row-reverse;

    .ant-form-item{
    margin: 0;
    }
`

const incidentPropertiesTypes = {
    groups: "groups",
    impactedFA: "impactedFA",
    impacts: "impacts",
    severities: "severities",
    status: "status",
    urgencies: "urgencies",
    categories: "categories",

    priorities: "priorities",
}

export const MapCreateIncidentModal = ({ isCreateIncidentModalOpen, setCreateIncidentModal, mapClickCoordinates, selectedPOI,
    addNewIncidentToMap, handleCreateIncidentSuccess
}) => {

    const [incidentForCreation, setIncidentForCreation] = useState(null);
    const { t } = useTranslation();

    const { genericItemsData, setCityService, cityService } = useGlobalData();

    const {
        isFetching: isCreatingIncident,
        refetch: refetchCreateIncident,
        isSuccess: isSuccessCreateIncident,
        isError: isErrorCreateIncident,
        error: errorCreateIncident,
        remove: removeCreateIncident
    } = useFetch(
        query.path.createIncident,
        "createIncident",
        incidentForCreation,
        false,
        "POST",
        query.middlewareCommandURL
    )

    /**
     * Create a new incident.
     */
    useEffect(() => {
        if (incidentForCreation) {
            refetchCreateIncident();
        }
    }, [incidentForCreation])

    /**
     * Sets the city service when the modal is opened.
     */
    useEffect(() => {
        if (selectedPOI && isCreateIncidentModalOpen) {
            setCityService(selectedPOI);
        }
    }, [selectedPOI, isCreateIncidentModalOpen])

    /**
     * Cleans the city service when the modal is closed.
     */
    useEffect(() => {
        if (!isCreateIncidentModalOpen) {
            setCityService(null);
        }
    }, [isCreateIncidentModalOpen]);

    const locale = t.language || "en-US";

    const date = new Date();
    const formattedDate = new Intl.DateTimeFormat(locale, {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
    }).format(date);

    /**
     * Handles the submission of an incident.
     *
     * @param {Object} incidentSubmission - The incident submission object.
     */
    const handleSubmission = (incidentSubmission) => {
        if (!incidentSubmission) return;

        const { cityService: cityServiceId, ...restValues } = incidentSubmission;

        const incidentToCreate = {
            ...restValues,
            tenant: cityService?.tenant,
            correlationID: selectedPOI?.id ?? cityServiceId,
        }

        setIncidentForCreation(incidentToCreate);
    }

    const recordForCreateIncident = useCreateIncidentRecord({
        selectedPOI,
        mapClickCoordinates,
        genericItemsData: genericItemsData,
        incidentPropertiesTypes,
    });

    useEffect(() => {
        if (!isCreateIncidentModalOpen) return;

        const successMessage = "notification.success_create_incident";
        const errorMessage = "notification.error_create_incident";

        handleNotificationWithAction(isCreatingIncident, isSuccessCreateIncident, isErrorCreateIncident, errorCreateIncident,
            successMessage, errorMessage, t,
            onCreateIncidentSuccess, onCreateIncidentSuccessOrError);

    }, [isErrorCreateIncident, isSuccessCreateIncident, isCreatingIncident]);

    /**
     * Callback function called when creating an incident is successful.
     */
    const onCreateIncidentSuccess = () => {
        setCreateIncidentModal(false);

        if (addNewIncidentToMap) {
            const incidentSerialized = serializeCreatedIncidentForTempAddition(incidentForCreation);
            addNewIncidentToMap(incidentSerialized)
        }

        if (handleCreateIncidentSuccess) {
            handleCreateIncidentSuccess();
        }
    }

    /**
     * Callback function called when creating an incident is successful or encounters an error.
     */
    const onCreateIncidentSuccessOrError = () => {
        removeCreateIncident();
    }

    /**
     * Retrieves the label corresponding to a given value from the incident properties data.
     *
     * @param {string} type - The type of incident property.
     * @param {string} value - The value to find the label for.
     * @param {object} incidentPropertiesData - The incident properties data object.
     * @returns {string} The label corresponding to the given value, or the value itself if no label is found.
     */
    const getLabelFromValue = (type, value, incidentPropertiesData) => {
        const items = incidentPropertiesData[type] || [];
        const item = items.find(item => item.value === value);
        return item ? item.label : value;
    }

    /**
     * Serializes a created incident for temporary addition.
     *
     * @param {Object} incident - The incident object to be serialized.
     * @returns {Object} - The serialized incident object.
     */
    const serializeCreatedIncidentForTempAddition = (incident) => {
        return {
            ...incident,
            priority: "",
            lastUpdate: (Date.now() / 1000),
            status: "In Progress",
            label: "Incident",
            name: "INCXXXXXXX",
            serviceName: cityService?.name,
            location: cityService?.name,
            title: btoa(incident.title),
            description: btoa(incident.description),
            address: "",
            subtype: "",
        }
    }

    return (
        <EditPopupConfigurations
            isToEdit={true}
            openModal={isCreateIncidentModalOpen}
            closeModal={() => {
                setCreateIncidentModal(false);
            }}
            selectedRecord={recordForCreateIncident}
            customWidth={700}
            customMaxHeight={400}
            onOkModal={handleSubmission}
            modalTitle={t('action.create_incident')}
            loading={isCreatingIncident}
            customHeader={(form) => {
                return <Col span={24} >
                    <HeaderContainer>
                        <Flex justify='space-between'>
                            <Flex align='center' gap={4}>
                                <Icon iconsize={'xs'} icon="Calendar" color="blue-4" />
                                <Text>{formattedDate}</Text>
                            </Flex>
                            {/* <ConfidentialWrapper>
                                {createField(form, { key: "confidential", name: t("input.confidential"), fieldTypeOf: fieldTypes.switch, })}
                            </ConfidentialWrapper> */}
                        </Flex>
                    </HeaderContainer>
                </Col>
            }}
        />

    )
};